<script lang="ts">
  import { onDestroy } from "svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiFileDocumentOutline } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ModalDialog from "@kontextwerk/web-sdk/ui/overlay/ModalDialog.svelte"
  import ProgressSteps, {
    type ProgressStep,
  } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import type {
    BacklogListImportOptions,
    BacklogDurationStrategy,
  } from "../../../../../../../../../../../../../../api/kehrblatt/backlog-lists"
  import { createImportDraft, type ImportDraft } from "./_shared/_logic/import-draft"
  import {
    WORK_ORDER_ACTION_TOOLTIP_CLASS,
    workOrderActionTooltips,
  } from "../../../../../../../../../../../../_shared/_logic/components-work-orders-action-tooltips"
  import InspectStep from "./steps/a_inspect"
  import WorkStep from "./steps/b_work"

  type Props = {
    organizationId: string
    orderTypes: OrderType[]
    disabled?: boolean
    onConfirm: (
      file: File,
      options: BacklogListImportOptions,
      signal: AbortSignal,
    ) => boolean | Promise<boolean>
  }
  const { organizationId, orderTypes, disabled = false, onConfirm }: Props = $props()
  let draft = $state<ImportDraft | null>(null)
  let lastStrategy = $state<BacklogDurationStrategy | undefined>(undefined)
  const close = () => {
    draft?.dispose()
    draft = null
  }
  const open = () => {
    if (disabled || draft) return

    draft = createImportDraft({
      organizationId: () => organizationId,
      orderTypes: () => orderTypes,
      initialStrategy: lastStrategy,
      onConfirm,
      onApplied: () => {
        const choice = draft?.options()

        if (choice?.contract_version === 2) lastStrategy = choice.duration_strategy

        close()
      },
    })
  }
  onDestroy(close)
  const steps: ProgressStep[] = $derived([
    {
      id: "inspect",
      label: "Datei prüfen",
      workflowState: draft?.state.step === 1 ? "completed" : "current",
      snippet: inspection,
    },
    {
      id: "work",
      label: "Arbeiten festlegen",
      workflowState: draft?.fileValid() ? "current" : "locked",
      snippet: work,
    },
  ])
</script>

{#snippet inspection()}
    {#if draft}<InspectStep {draft} />{/if}
{/snippet}
{#snippet work()}
    {#if draft}<WorkStep {draft} {orderTypes} />{/if}
{/snippet}

<div class="backlog-list-upload">
    <button
    class="source-action"
    type="button"
    aria-label="Rückstandsliste wählen"
    {disabled}
    data-tooltip={workOrderActionTooltips.batchLoadBacklogList}
    data-tooltip-class={WORK_ORDER_ACTION_TOOLTIP_CLASS}
    onclick={open}
  >
        <span class="source-icon"><Icon path={mdiFileDocumentOutline} /></span>
        <span class="source-copy"
      ><strong>Rückstandsliste wählen</strong><span>Keine Rückstandsliste geladen</span></span
    >
    </button>
</div>
{#if draft}
    <div class="backlog-list-import">
        <ModalDialog appearance="plain" title="Rückstandsliste übernehmen" size="large" onClose={close}>
            <div class="import-dialog">
                <ProgressSteps
          {steps}
          showControls={false}
          darkMode={false}
          syncUrl={false}
          statusMode="explicit"
          navigationMode="guarded"
          selectedIndex={draft.state.step}
          onSelectedIndexChange={(index) => {
            if (!draft.state.submitting && (index === 0 || draft.fileValid()))
              draft.state.step = index
          }}
        />
                {#if draft.state.error}<p
            class="import-error"
            role="alert"
            style="color: var(--text-invers-100) !important;">{draft.state.error}</p
          >{/if}
            </div>
            {#snippet footerSnippet()}
                <div class="import-actions">
                    {#if draft.state.step === 1}
                        <CTA
              type="ghostButton"
              darkMode={false}
              content="Zurück"
              disabled={draft.state.submitting}
              onClick={() => {
                draft.state.step = 0
              }}
            />
                        <CTA
              type="primaryButton"
              darkMode={false}
              content={draft.state.submitting ? "Wird übernommen…" : "In Planung übernehmen"}
              disabled={disabled || draft.state.submitting || !draft.options()}
              onClick={draft.submit}
            />
                    {:else}
                        <CTA
              type="primaryButton"
              darkMode={false}
              content="Weiter"
              disabled={disabled || !draft.fileValid()}
              onClick={() => {
                draft.state.step = 1
              }}
            />
                    {/if}
                </div>
            {/snippet}
        </ModalDialog>
    </div>
{/if}

<style>
  .backlog-list-upload {
    margin-top: 0.9rem;
    display: flex;
    min-width: 0;
    width: 100%;
  }
  .source-action {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    min-height: 5.4rem;
    width: 100%;
    min-width: 0;
    padding: 1rem;
    border: 1px solid color-mix(in srgb, var(--text-100) 28%, transparent);
    background: color-mix(in srgb, var(--bg-200) 46%, transparent);
    color: var(--text-100);
    text-align: left;
    cursor: pointer;
  }
  .source-action:hover:not(:disabled) {
    border-color: color-mix(in srgb, var(--primary-100) 72%, var(--text-100));
    background: color-mix(in srgb, var(--primary-100) 18%, var(--bg-200));
  }
  .source-action:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  .source-icon {
    width: 2.4rem;
    height: 2.4rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid color-mix(in srgb, var(--text-100) 24%, transparent);
    flex: 0 0 auto;
  }
  .source-copy {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .source-copy strong {
    font-family: var(--font-family-bold, "Outfit-Bold", sans-serif);
    text-transform: uppercase;
    line-height: 1.1;
  }
  .source-copy > span {
    color: var(--text-200);
    font-size: 0.9rem;
    line-height: 1.25;
  }
  .import-dialog {
    --progress-content-padding-left: var(--kw-app-content-inset);
    --progress-content-padding-right: var(--kw-app-content-inset);
    --progress-content-padding-top: 1.25rem;
    --progress-content-padding-bottom: var(--kw-app-content-inset);
    color: var(--text-invers-100) !important;
    min-width: 0;
  }
  .backlog-list-import :global(.modal__body) {
    padding: 0;
  }
  .import-dialog :global(.progress-track > .tab-list) {
    margin-inline-start: 0;
  }
  .import-dialog :global(.progress-track .step-tab:first-child) {
    padding-inline-start: var(--kw-app-content-inset);
  }
  .backlog-list-import :global(button.cta:is(.ghostButton, .ghostButtonReverse).lightMode),
  .backlog-list-import :global(button.cta:is(.ghostButton, .ghostButtonReverse).lightMode p),
  .backlog-list-import
    :global(button.cta:is(.ghostButton, .ghostButtonReverse).lightMode .kw-safe-html) {
    color: var(--text-invers-100) !important;
  }
  .backlog-list-import
    :global(button.cta:is(.ghostButton, .ghostButtonReverse).lightMode:hover:not(:disabled)),
  .backlog-list-import
    :global(button.cta:is(.ghostButton, .ghostButtonReverse).lightMode:hover:not(:disabled) p),
  .backlog-list-import
    :global(
      button.cta:is(.ghostButton, .ghostButtonReverse).lightMode:hover:not(:disabled) .kw-safe-html
    ) {
    color: var(--neutral-white) !important;
  }
  .backlog-list-import :global(button.cta.primaryButton),
  .backlog-list-import :global(button.cta.primaryButton p),
  .backlog-list-import :global(button.cta.primaryButton .kw-safe-html) {
    color: var(--neutral-white) !important;
  }
  .import-error {
    margin: 0 var(--kw-app-content-inset) var(--kw-app-content-inset);
    padding: 0.75rem;
    color: var(--text-invers-100) !important;
    border-left: 3px solid var(--error-100);
  }
  .import-actions {
    display: flex;
    width: 100%;
    gap: 0.6rem;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
</style>
